/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2015-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.pageViewModule')
  .controller('PageViewFooterController', [
    'unit',
    '$anchorScroll',
    '$location',
    '$scope',
    '$state',
    '$timeout',
    'BulbAuthService',
    'BulbBusyService',
    'BulbCommentService',
    'BulbModalService',
    'BulbRecommendService',
    'BulbUnitService',
    'Restangular',
    'SerialSaveService',
    'UnitStateStore',
    'AUTH',
    'CONFIG',
    '_',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    PageViewFooterController]);

/**
 * AngularJS controller for footer of a page view.
 */
function PageViewFooterController(
  unit,
  $anchorScroll,
  $location,
  $scope,
  $state,
  $timeout,
  BulbAuthService,
  BulbBusyService,
  BulbCommentService,
  BulbModalService,
  BulbRecommendService,
  BulbUnitService,
  Restangular,
  SerialSaveService,
  UnitStateStore,
  AUTH,
  CONFIG,
  _,
) {
  const that = this;

  this.cleverIntegrationEnabled = CONFIG.features.cleverIntegrationEnabled;

  this.dataLayer = (window.dataLayer || []);
  this.currentUserPrefs = BulbAuthService.currentUser.prefs;

  this.unit = unit;
  this.canEdit = BulbAuthService.currentUser.hasPermission(this.unit, AUTH.PERMISSIONS.UPDATE);
  this.assignmentFolderId = unit.assignmentFolderId;
  this.showQuestions = false;
  this.loadedQuestions = false;
  this.focusQuestion = false;
  this.commentsOnPage = 0;
  this.snapshotView = $state.current.data.snapshotView || false;

  BulbUnitService.fetchParent().then(() => {
    that.parentUnit = BulbUnitService.parentUnit;
    that.previousUnit = BulbUnitService.previousSibling;
    that.nextUnit = BulbUnitService.nextSibling;
  });

  // This will only happen once per page, and requires the timeout since the comments are being loaded in after the
  // page is up.
  $timeout(() => {
    // Watcher to keep an eye on number of comments on page.
    $scope.$watch(() => BulbCommentService.getPageCommentsCount(), (value) => {
      that.commentsOnPage = value;
    });

    if (BulbCommentService.pageHasPrivateComments() && that.unit.unitType === 'DraftPageUnit'
      && !that.unit.privateCommentModalShown && that.canEdit) {
      that.showPrivateCommentModal();
    }

    if (BulbCommentService.pageHasArchivedComments() && that.unit.unitType === 'DraftPageUnit'
      && !that.unit.archiveCommentModalShown && that.canEdit) {
      that.showArchiveCommentModal();
    }
  }, 1000);

  this.showPrivateCommentModal = function showPrivateCommentModal() {
    const modalOptions = {
      titleCode: 'private_comment_disabled_modal',
      bodyCode: 'private_comment_disabled_modal_body',
      okButtonCode: 'modal_convert',
      showOkButton: true,
      showCancelButton: true,
      fontAwesomeCode: 'fas fa-exclamation-triangle',
      windowClass: 'updated-modal-design private-comment-modal',
    };

    BulbModalService.show(modalOptions).then(() => {
      SerialSaveService.save({
        save: () => Restangular.one('comment', that.unit.id)
          .customPOST({ confirm: true }, 'convert?conversionType=private')
          .then(() => {
            $state.reload();
          }),
      });
    }, () => {
      SerialSaveService.save({
        save: () => Restangular.one('comment', that.unit.id)
          .customPOST({ confirm: false }, 'convert?conversionType=private'),
      });
    });
  };

  this.showArchiveCommentModal = function showArchiveCommentModal() {
    const modalOptions = {
      titleCode: 'archive_comment_disabled_modal',
      bodyCode: 'archive_comment_disabled_modal_body',
      okButtonCode: 'modal_convert',
      showOkButton: true,
      showCancelButton: true,
      fontAwesomeCode: 'fas fa-exclamation-triangle',
      windowClass: 'updated-modal-design private-comment-modal',
    };

    BulbModalService.show(modalOptions).then(() => {
      SerialSaveService.save({
        save: () => Restangular.one('comment', that.unit.id)
          .customPOST({ confirm: true }, 'convert?conversionType=archive')
          .then(() => {
            $state.reload();
          }),
      });
    }, () => {
      SerialSaveService.save({
        save: () => Restangular.one('comment', that.unit.id)
          .customPOST({ confirm: false }, 'convert?conversionType=archive'),
      });
    });
  };

  /**
   * toggle the display of the questions section.  If the section is being displayed for the first time, the
   * questions are retrieved from the server.
   *
   * @param focus Set to true if the 'new question' input should be focused automatically.
   */
  this.toggleQuestions = function toggleQuestions(focus) {
    if (that.showQuestions) {
      that.showQuestions = false;
    } else {
      this.focusQuestion = focus;
      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.unit.id).getList('questions').then((questions) => {
          // FUTURE : this should probably be sorted locally, rather than relying on the order from the
          // server.
          that.questions = questions;
          that.loadedQuestions = true;
          that.showQuestions = true;

          // if there is an anchor to any of the questions, scroll to it as soon as the rendering is complete.
          // $timeout is used instead of $evalAsync as it fires after rendering.
          const hash = $location.hash();
          if (hash && hash.indexOf('questions') > -1) {
            $timeout(() => {
              $anchorScroll();
            });
          }
        }),
      });
    }
  };

  /**
   * Delete the indicated question.
   * @param question Question to be deleted.
   */
  this.deleteQuestion = function deleteQuestion(question) {
    const modalOptions = {
      titleCode: 'pageview_delete_question_confirm_title',
      bodyCode: 'pageview_delete_question_confirm_body',
      okButtonCode: 'pageview_delete_question',
    };

    BulbModalService.show(modalOptions).then(() => {
      SerialSaveService.save({
        showBusy: true,
        save: () => question.remove().then(() => {
          _.pull(that.questions, question);
        }),
      });
    });
  };

  /**
   * Check if page redirect after login if someone like page on page load.
   */
  $scope.$on('$viewContentLoaded', () => {
    const isAuthorized = localStorage.getItem('isAuthorized');
    if (isAuthorized === 'isAuthorized') {
      that.toggleRecommend();
    }
  });

  // Toggles whether the page is recommended by the user.
  this.toggleRecommend = function toggleRecommend() {
    localStorage.removeItem('isAuthorized');
    if (BulbAuthService.currentUser.isAuthenticated()) {
      // We need to set the liked status first before sending it off to the server, as slower connections
      // maybe able to send multiple likes. We can then catch if the request failed and if so, revert.
      if (that.unit.likedByUser) {
        that.unit.likedByUser = false;
        // eslint-disable-next-line no-plusplus
        that.unit.likeCount--;

        BulbRecommendService.deleteRecommend(that.unit).catch(() => {
          that.unit.likedByUser = true;
          // eslint-disable-next-line no-plusplus
          that.unit.likeCount++;
        });
      } else {
        that.unit.likedByUser = true;
        // eslint-disable-next-line no-plusplus
        that.unit.likeCount++;

        BulbRecommendService.createRecommend(that.unit).catch(() => {
          that.unit.likedByUser = false;
          // eslint-disable-next-line no-plusplus
          that.unit.likeCount--;
        });
      }
    } else {
      const targetUrl = `/u/${this.unit.path}`;
      const unitId = this.unit.id;
      BulbModalService.open({
        titleCode: '',
        template: '<bulb-recommend-login></bulb-recommend-login>',
        windowClass: 'bulb-aero-modal-body bulb-pageview-login-modal',
        closeOnClick: true,
        showOkButton: false,
        showCancelButton: false,
        // eslint-disable-next-line @typescript-eslint/no-shadow
        controller: ['$scope', function modalController($modalControllerScope) {
          $modalControllerScope.targetUrl = targetUrl;
          $modalControllerScope.unitId = unitId;
        }],
      });
    }
  };

  // This function is used to apply focus to the comment section.
  this.applyFocusToCommentSection = function applyFocusToCommentSection() {
    $('.bulb-comment-lower-container textarea').first().focus();
  };

  /**
   * Create a new question based on the contents of the question input field.
   */
  this.submitQuestion = function submitQuestion() {
    const newQuestion = {
      questionText: this.newQuestionText,
      unitId: this.unit.id,
    };

    SerialSaveService.save({
      showBusy: true,
      save: () => that.questions.post(newQuestion).then((addedQuestion) => {
        that.questions.unshift(addedQuestion);
        delete that.newQuestionText;
      }),
    });
  };

  this.submitAnswer = function submitAnswer(question) {
    const patch = {
      answerText: question.answerText,
    };

    SerialSaveService.save({
      showBusy: true,
      save: () => question.patch(patch).then((savedQuestion) => {
        that.questions[that.questions.indexOf(question)] = savedQuestion;
      }),
    });
  };

  this.startAnswering = function startAnswering(question) {
    question.answering = true;
    question.originalAnswerText = question.answerText;
  };

  this.cancelAnswer = function cancelAnswer(question) {
    question.answering = false;
    question.answerText = question.originalAnswerText;
    delete question.originalAnswerText;
  };

  /**
   * Method which sends the anonymous user to sign in, and then directs the user back to the page when they are
   * done signing in.
   */
  this.signIn = function signIn() {
    const targetUrl = `/u/${this.unit.path}#questions`;
    $state.go('signIn', { targetUrl });
  };

  const ShareStatus = UnitStateStore.getShareStatusEnum();
  const shareStatusSub = UnitStateStore.shareStatus$.subscribe((shareStatus) => {
    if (shareStatus === ShareStatus.SHARED && !that.unit.publishedDate) {
      that.unit.publishedDate = new Date();
      that.unit.unitType = 'PublishedPageUnit';
    }
  });

  $scope.$on('$destroy', () => {
    shareStatusSub.unsubscribe();
  });
}
